import React, { useState } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import {
    Button,
    Icon,
    TextField,
    Typography,
    withStyles
} from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import whatsappLogo from '../icons/whatsapp.svg';
import Modal from './Modal';

const styles = () => ({
    modalBody: {
        textAlign: 'left'
    },
    emailInput: {
        width: 210
    },
    sendButton: {
        height: '2.5rem',
        margin: '0 0 0 10px'
        // padding: '0.4em'
    },
    error: {
        margin: '20px 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    paragraph: {
        marginTop: '1rem'
    },
    asterisk: {
        display: 'none'
    }
});

const SendTicketModal = ({
    show,
    setShow,
    classes,
    customerTicketId,
    ticketName
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [ticketSent, setTicketSent] = useState(false);

    const closeModal = ({ sent = false }) => {
        if (loading) {
            return;
        }
        setEmail('');
        setError('');
        setLoading(false);
        setTicketSent(sent);
        setShow(false);
    };

    const handleEmailSubmit = async event => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            await axios.post(
                `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-ticket/send/${customerTicketId}`,
                {
                    email
                },
                {
                    headers
                }
            );

            setLoading(false);
            setTicketSent(true);
        } catch (e) {
            console.error(e);
            setLoading(false);
            setError('Ticket could not be sent. Please try again later.');
        }
    };

    const serviceName = ticketName ? `${ticketName} ` : '';
    const whatsAppMessage = `Your ${serviceName}bus ticket is available here: ${process.env.REACT_APP_VIEW_TICKET_BASE_URL}${customerTicketId}`;
    const whatsAppLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        whatsAppMessage
    )}`;

    return (
        <Modal title="Send Ticket" show={show} onCancel={closeModal}>
            <div className={classes.modalBody}>
                {ticketSent ? (
                    <>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                            style={{ fontSize: '1.3em' }}
                        >
                            <span aria-label="tick" role="img">
                                ✅
                            </span>
                            &nbsp;Ticket sent to <strong>{email}</strong>.
                        </Typography>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            The passenger will receive their ticket in the next
                            couple of minutes.
                        </Typography>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Still not received it? Check the following:
                        </Typography>
                        <ul>
                            <li>The email address was entered correctly.</li>
                            <li>
                                The message was delivered to the spam folder.
                            </li>
                            <li>The inbox is not full and still has space.</li>
                        </ul>
                        <div>
                            <Button
                                className={classes.paragraph}
                                variant="outlined"
                                color="primary"
                                onClick={closeModal}
                            >
                                Close
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Purchased a ticket on behalf of a passenger? Send
                            the ticket directly to the recipient using WhatsApp
                            or email.
                        </Typography>

                        <div
                            style={{
                                marginTop: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            id="send-ticket-whatsapp-heading"
                        >
                            <img
                                src={whatsappLogo}
                                alt="WhatsApp"
                                style={{ width: '24px', height: '24px' }}
                            />{' '}
                            <Typography variant="h5">WhatsApp</Typography>
                        </div>

                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Send a private ticket link to the passenger via a
                            WhatsApp message.
                        </Typography>

                        <Button
                            className={classes.paragraph}
                            variant="outlined"
                            color="primary"
                            component="a"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={whatsAppLink}
                        >
                            Send WhatsApp
                        </Button>

                        <Typography style={{ marginTop: '1rem' }} variant="h5">
                            <Icon
                                color="primary"
                                style={{
                                    padding: '4px 0'
                                }}
                            >
                                email
                            </Icon>{' '}
                            Email
                        </Typography>

                        <Typography
                            className={classes.paragraph}
                            variant="body2"
                        >
                            Send a private ticket link by email to open directly
                            on the passenger&apos;s device.
                        </Typography>

                        <form
                            className={classes.paragraph}
                            onSubmit={handleEmailSubmit}
                        >
                            <TextField
                                autoComplete="off"
                                size="small"
                                className={classes.emailInput}
                                id="email"
                                type="email"
                                label="Passenger email"
                                value={email}
                                onChange={({ target }) =>
                                    setEmail(target.value.trim())
                                }
                                variant="outlined"
                                disabled={loading}
                                required
                                InputLabelProps={{
                                    classes: {
                                        asterisk: classes.asterisk
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.sendButton}
                                disabled={loading}
                                type="submit"
                            >
                                Send
                            </Button>
                        </form>
                    </>
                )}
            </div>
            {error && (
                <Typography className={classes.error} variant="body2">
                    {error}
                </Typography>
            )}
        </Modal>
    );
};

export default withStyles(styles)(SendTicketModal);
